import React, { FC } from 'react'
import { RouteParams } from '@sketch/modules-common'

import {
  useVersioning,
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
} from '../../../../versioning'
import { useGetSurroundingFramesQuery } from '@sketch/gql-types'
import { PaginationControls } from '@sketch/components'
import { useHistory, useLocation, useRouteMatch } from 'react-router'
import { NavbarItem } from '../../../components/NavbarItem'
import { Wrapper } from './FramePaginationControls.styles'

interface LocationState {
  fromOverview?: boolean
}

interface FramePaginationControlsProps {
  current: number
  total: number
  showNumbers?: boolean
}

/**
 * Navigate between frames using keyboard left/right arrows
 */
export const FramePaginationControls: FC<FramePaginationControlsProps> = ({
  current,
  total,
  showNumbers,
  ...rest
}) => {
  const match = useRouteMatch<RouteParams<'FRAME'>>()
  const location = useLocation<LocationState>()
  const history = useHistory()

  const {
    versionShortId,
    getPathname,
    setArtboardThumbnail,
    artboardThumbnail,
  } = useVersioning()
  const { shareID, frameUUID } = match.params

  const fromOverview = location.state?.fromOverview

  const navigateTo = (pathname: string) =>
    history.push({
      pathname: pathname,
      state: { fromOverview },
    })

  const { data: frameData, error } = useGetSurroundingFramesQuery({
    variables: {
      frameUUID,
      documentVersionShortId: versionShortId,
      shareIdentifier: shareID,
    },
  })

  if (isUpgradeToLatestNeeded(error)) {
    return <UpgradeToLatestVersion error={error} />
  }

  const nextUUID = frameData?.frame?.next?.uuid
  const previousUUID = frameData?.frame?.previous?.uuid

  const currentPage = current ? current + 1 : 1

  const clearThumbnail = () =>
    artboardThumbnail !== '' && setArtboardThumbnail('')

  const handleClickNext = nextUUID
    ? () => {
        const nextPathname = getPathname({
          routeKey: 'FRAME',
          routeParams: {
            shareID: shareID,
            frameUUID: nextUUID,
          },
        })

        clearThumbnail()
        navigateTo(nextPathname)
      }
    : undefined

  const handleClickPrevious = previousUUID
    ? () => {
        const previousPathname = getPathname({
          routeKey: 'FRAME',
          routeParams: {
            shareID: shareID,
            frameUUID: previousUUID,
          },
        })
        clearThumbnail()
        navigateTo(previousPathname)
      }
    : undefined

  return (
    <Wrapper>
      <NavbarItem>
        <PaginationControls
          current={currentPage}
          total={total}
          onClickNext={handleClickNext}
          onClickPrevious={handleClickPrevious}
          onlyKeyboardNavigation={!showNumbers}
          {...rest}
        />
      </NavbarItem>
    </Wrapper>
  )
}
